import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { LatLng } from 'src/app/models/latlng';
import { AccountService } from 'src/app/services/account/account.service';
import { ConsoleLoggerService } from 'src/app/services/logging/console-logger.service';
import { LanguageService } from 'src/app/services/account/language.service';
import { MapService, ShowOnMapEnum } from 'src/app/services/map/map.service';
import { MapPoiDrawerService } from 'src/app/services/map/map-poi-drawer.service';
import { AddressType, SettingsService } from 'src/app/services/account/settings.service';
import { MapElementType } from 'src/app/models/map-element-type';
import { SearchedRouteElement } from '../../../models/searched-route-element';
import { Address } from 'src/app/models/address';
import { SearchComponent } from '../../search/search.component';

@Component({
  standalone: true,
  selector: 'app-address-panel',
  templateUrl: './address-panel.component.html',
  imports: [SearchComponent],
  styleUrls: ['./address-panel.component.scss']
})
export class AddressComponent implements OnInit, OnDestroy {

  @Input() addressType: string = "";
  public inputValue: string = "";
  public selectedAddress: Address | null = null;

  constructor(public accountService: AccountService,
    public settingsService: SettingsService,
    private readonly consoleLoggerService: ConsoleLoggerService,
    private readonly mapService: MapService,
    private readonly mapPoiDrawerService: MapPoiDrawerService,
    public languageService: LanguageService) { }

  ngOnInit(): void {

    if (this.addressType === "home") {
      let homeAddress = this.settingsService.getHomeAddress();
      if (homeAddress?.Description) {
        this.inputValue = homeAddress.Description;
        this.mapService.setSearchedMapElement(MapElementType.Address,
          new Address(
            AddressType.Home,
            homeAddress.Description,
            new LatLng(homeAddress.Location.lat, homeAddress.Location.lng),
            homeAddress.BoundingBox
          ));
        this.showAddressOnMap(homeAddress.Location.lat, homeAddress.Location.lng, homeAddress.Description);
      }
    }
    if (this.addressType === "work") {
      let workAddress = this.settingsService.getWorkAddress();
      if (workAddress?.Description) {
        this.inputValue = workAddress.Description;
        this.mapService.setSearchedMapElement(MapElementType.Address,
          new Address(
            AddressType.Work,
            workAddress.Description,
            new LatLng(workAddress.Location.lat, workAddress.Location.lng),
            workAddress.BoundingBox
          ));
        this.showAddressOnMap(workAddress.Location.lat, workAddress.Location.lng, workAddress.Description);
      }
    }
  }

  ngOnDestroy(): void {
    // Runs when the panel closes via Save or Back (the *ngIf host removes it),
    // so the read-only address card is torn down together with the panel.
    this.mapPoiDrawerService.removePoi();
  }

  public searchChangedHandler(event: SearchedRouteElement): void {
    if (this.addressType === "home") {
      this.selectedAddress = new Address(
        AddressType.Home,
        event.display_name,
        new LatLng(event.lat, event.lon),
        event.boundingbox
      );
    }
    if (this.addressType === "work") {
      this.selectedAddress = new Address(
        AddressType.Work,
        event.display_name,
        new LatLng(event.lat, event.lon),
        event.boundingbox
      );
    }
    this.mapService.setSearchedMapElement(MapElementType.Address, this.selectedAddress);
    this.showAddressOnMap(event.lat, event.lon, event.display_name);
  }

  // Reveals the address on the map with a read-only POI card (no GO button)
  // that cannot be dismissed by clicking the map — mirrors how a charging
  // station is revealed from the settings dialog.
  private showAddressOnMap(lat: number, lng: number, description: string): void {
    this.mapService.centerMap(15, lat, lng);
    this.mapPoiDrawerService.drawPoi(lat, lng, { address: description },
      { showGoButton: false, dismissOnMapClick: false });
  }

  public save(): void {
    if (this.selectedAddress) {
      if (this.addressType === "home") {
        this.settingsService.setHomeAddress(this.selectedAddress);
      }
      if (this.addressType === "work") {
        this.settingsService.setWorkAddress(this.selectedAddress);
      }
      this.accountService.setUserAddresses().subscribe();
    }
    this.mapService.ObservableShowOnMap.next({ type: ShowOnMapEnum.BackSettings });
  }

  public goBack(): void {
    this.mapService.ObservableShowOnMap.next({ type: ShowOnMapEnum.BackSettings });
  }
}
